import React from 'react';
import { Box, Typography, Paper, useTheme, useMediaQuery, Alert, Chip } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import Battery80Icon from '@mui/icons-material/Battery80';
import Battery60Icon from '@mui/icons-material/Battery60';
import Battery50Icon from '@mui/icons-material/Battery50';
import Battery30Icon from '@mui/icons-material/Battery30';
import Battery20Icon from '@mui/icons-material/Battery20';

const StaminaChart = ({ workoutData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!workoutData?.stamina_score || !workoutData?.stamina_index) {
    return (
      <Box p={3}>
        <Alert severity="info">
          {t('staminaNotAvailable', 'Stamina analysis not available for this workout')}
        </Alert>
      </Box>
    );
  }

  const staminaScore = workoutData.stamina_score;
  const staminaIndex = workoutData.stamina_index;
  const chunks = workoutData.chunks || [];

  // Format timeline data from chunks
  // Display at 30-second intervals to match backend grouping
  const DISPLAY_INTERVAL = 30; // seconds - matches backend MIN_GROUP_DURATION
  
  const validChunks = chunks.filter(chunk => 
    chunk.stamina_score !== null && chunk.stamina_score !== undefined
  );
  
  // Group by 30-second buckets and take one representative per bucket
  const bucketMap = new Map();
  validChunks.forEach(chunk => {
    const bucket = Math.floor(chunk.timestamp / DISPLAY_INTERVAL);
    if (!bucketMap.has(bucket)) {
      bucketMap.set(bucket, chunk);
    }
  });
  
  const chartData = Array.from(bucketMap.values())
    .sort((a, b) => a.timestamp - b.timestamp)
    .map(chunk => ({
      time: formatTime(chunk.timestamp),
      timeSeconds: chunk.timestamp,
      staminaScore: chunk.stamina_score,
      power: chunk.avg_power,
      gradient: chunk.avg_gradient
    }));

  // Calculate polynomial regression for a given degree
  const fitPolynomial = (data, degree) => {
    if (data.length < degree + 1) return null;
    
    const n = data.length;
    const x = data.map((d, i) => i);
    const y = data.map(d => d.staminaScore);
    
    // Build Vandermonde matrix
    const X = [];
    for (let i = 0; i < n; i++) {
      const row = [];
      for (let j = 0; j <= degree; j++) {
        row.push(Math.pow(x[i], j));
      }
      X.push(row);
    }
    
    // Solve using Normal Equations: (X^T * X) * β = X^T * y
    const XT = transpose(X);
    const XTX = matrixMultiply(XT, X);
    const XTy = matrixVectorMultiply(XT, y);
    const coefficients = gaussianElimination(XTX, XTy);
    
    // Calculate predicted values
    const predicted = x.map(xi => {
      let value = 0;
      for (let j = 0; j <= degree; j++) {
        value += coefficients[j] * Math.pow(xi, j);
      }
      return value;
    });
    
    // Calculate R² (coefficient of determination)
    const yMean = y.reduce((sum, val) => sum + val, 0) / n;
    const ssTotal = y.reduce((sum, val) => sum + Math.pow(val - yMean, 2), 0);
    const ssResidual = y.reduce((sum, val, i) => sum + Math.pow(val - predicted[i], 2), 0);
    const rSquared = 1 - (ssResidual / ssTotal);
    
    return { coefficients, rSquared, predicted };
  };
  
  // Find best polynomial degree by comparing R² values
  const findBestPolynomial = (data) => {
    if (!data || data.length < 2) return null;
    
    let bestDegree = 1;
    let bestRSquared = -Infinity;
    let bestResult = null;
    
    // Try degrees 1 through 6
    for (let degree = 1; degree <= 6; degree++) {
      if (data.length < degree + 1) continue;
      
      const result = fitPolynomial(data, degree);
      if (result && result.rSquared > bestRSquared) {
        bestRSquared = result.rSquared;
        bestDegree = degree;
        bestResult = result;
      }
    }
    
    if (!bestResult) return null;
    
    // Generate trend data using best polynomial
    return {
      degree: bestDegree,
      rSquared: bestRSquared,
      data: data.map((d, i) => ({
        ...d,
        trend: Math.max(0, Math.min(150, bestResult.predicted[i])) // Clamp between 0-150%
      }))
    };
  };
  
  // Matrix transpose
  const transpose = (matrix) => {
    return matrix[0].map((_, i) => matrix.map(row => row[i]));
  };
  
  // Matrix multiplication
  const matrixMultiply = (a, b) => {
    const result = [];
    for (let i = 0; i < a.length; i++) {
      result[i] = [];
      for (let j = 0; j < b[0].length; j++) {
        let sum = 0;
        for (let k = 0; k < a[0].length; k++) {
          sum += a[i][k] * b[k][j];
        }
        result[i][j] = sum;
      }
    }
    return result;
  };
  
  // Matrix-vector multiplication
  const matrixVectorMultiply = (matrix, vector) => {
    return matrix.map(row => 
      row.reduce((sum, val, i) => sum + val * vector[i], 0)
    );
  };
  
  // Gaussian elimination for solving linear systems
  const gaussianElimination = (A, b) => {
    const n = A.length;
    const Ab = A.map((row, i) => [...row, b[i]]);
    
    // Forward elimination
    for (let i = 0; i < n; i++) {
      // Partial pivoting
      let maxRow = i;
      for (let k = i + 1; k < n; k++) {
        if (Math.abs(Ab[k][i]) > Math.abs(Ab[maxRow][i])) {
          maxRow = k;
        }
      }
      [Ab[i], Ab[maxRow]] = [Ab[maxRow], Ab[i]];
      
      // Make all rows below this one 0 in current column
      for (let k = i + 1; k < n; k++) {
        const factor = Ab[k][i] / Ab[i][i];
        for (let j = i; j <= n; j++) {
          Ab[k][j] -= factor * Ab[i][j];
        }
      }
    }
    
    // Back substitution
    const x = new Array(n);
    for (let i = n - 1; i >= 0; i--) {
      x[i] = Ab[i][n];
      for (let j = i + 1; j < n; j++) {
        x[i] -= Ab[i][j] * x[j];
      }
      x[i] /= Ab[i][i];
    }
    
    return x;
  };
  
  // Calculate best polynomial fit
  const polynomialResult = findBestPolynomial(chartData);
  const trendData = polynomialResult?.data || null;
  const bestDegree = polynomialResult?.degree || 0;
  const rSquared = polynomialResult?.rSquared || 0;

  // Helper function to format time
  function formatTime(seconds) {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) {
      return `${hrs}h${mins}m`;
    }
    return `${mins}min`;
  }

  // Get rating and message from index
  const getRating = (index) => {
    if (index === 5) return 'excellent';
    if (index === 4) return 'good';
    if (index === 3) return 'fair';
    if (index === 2) return 'below average';
    return 'poor';
  };

  const getMessage = (score, index) => {
    const formattedScore = score.toFixed(1);
    if (index === 5) return t('staminaExcellent', { score: formattedScore });
    if (index === 4) return t('staminaGood', { score: formattedScore });
    if (index === 3) return t('staminaFair', { score: formattedScore });
    if (index === 2) return t('staminaBelowAverage', { score: formattedScore });
    return t('staminaPoor', { score: formattedScore });
  };

  const rating = getRating(staminaIndex);
  const message = getMessage(staminaScore, staminaIndex);

  // Get color based on rating
  const getRatingColor = (rating) => {
    switch (rating) {
      case 'excellent': return colors.greenAccent[500];
      case 'good': return colors.greenAccent[600];
      case 'fair': return colors.redAccent[400];
      case 'poor': return colors.redAccent[500];
      default: return colors.grey[500];
    }
  };

  // Get severity for Alert
  const getAlertSeverity = (rating) => {
    switch (rating) {
      case 'excellent':
      case 'good':
        return 'success';
      case 'fair':
        return 'warning';
      case 'poor':
        return 'error';
      default:
        return 'info';
    }
  };

  // Get battery icon based on score
  const getScoreIcon = () => {
    if (staminaScore === null) return <Battery50Icon />;
    if (staminaScore >= 100) return <BatteryFullIcon />;
    if (staminaScore >= 95) return <Battery80Icon />;
    if (staminaScore >= 90) return <Battery60Icon />;
    if (staminaScore >= 85) return <Battery50Icon />;
    if (staminaScore >= 80) return <Battery30Icon />;
    return <Battery20Icon />;
  };

  return (
    <Box>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          backgroundColor: colors.grey[800],
          borderRadius: '8px',
          mb: 3
        }}
      >
        {/* Header with Score */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" color={colors.grey[100]} fontWeight="bold">
            {t('staminaAnalysis', 'Stamina Analysis')}
          </Typography>
          
          <Box display="flex" alignItems="center" gap={2}>
            <Box textAlign="right">
              <Typography variant="caption" color={theme.palette.mode === 'dark' ? colors.grey[400] : colors.grey[300]}>
                {t('overallStamina', 'Overall Stamina')}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {getScoreIcon()}
                <Typography variant="h3" color={getRatingColor(rating)} fontWeight="bold">
                  {staminaScore.toFixed(1)}%
                </Typography>
              </Box>
            </Box>
            <Chip 
              label={rating.toUpperCase()}
              sx={{
                backgroundColor: getRatingColor(rating),
                color: colors.grey[900],
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}
            />
          </Box>
        </Box>

        {/* Interpretation Message */}
        <Alert severity={getAlertSeverity(rating)} sx={{ mb: 3 }}>
          <Typography variant="body2">{message}</Typography>
        </Alert>

        {/* Stamina Timeline Chart */}
        {chartData.length > 0 && (
          <Box>
            <Typography variant="h6" color={colors.grey[300]} mb={2}>
              {t('staminaTimeline', 'Stamina Score Over Time')}
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart
                data={trendData || chartData}
                margin={isMobile ? { top: 5, right: 5, left: 0, bottom: 5 } : { top: 5, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
                <XAxis
                  dataKey="time"
                  stroke={colors.grey[300]}
                  tick={{ fill: colors.grey[300] }}
                />
                <YAxis
                  stroke={colors.grey[300]}
                  domain={[70, 110]}
                  width={isMobile ? 0 : 55}
                  tick={isMobile ? false : { fill: colors.grey[300] }}
                  axisLine={!isMobile}
                  tickLine={!isMobile}
                  label={isMobile ? undefined : {
                    value: t('staminaScore', 'Stamina Score (%)'),
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: colors.grey[300] }
                  }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: colors.grey[800], 
                    border: `1px solid ${colors.grey[700]}`,
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: colors.grey[100] }}
                  itemStyle={{ color: colors.grey[100] }}
                  formatter={(value, name) => {
                    if (name === 'staminaScore') return [value.toFixed(1) + '%', t('staminaScore', 'Stamina Score')];
                    if (name === 'trend') return [value.toFixed(1) + '%', t('staminaTrend', 'Trend')];
                    return [value.toFixed(1), name];
                  }}
                />
                <Legend
                  wrapperStyle={{ color: colors.grey[300] }}
                  verticalAlign="bottom"
                />
                
                {/* Baseline at 100% */}
                <ReferenceLine 
                  y={100} 
                  stroke={colors.grey[500]} 
                  strokeDasharray="3 3"
                  label={{ 
                    value: t('baseline', 'Baseline'), 
                    fill: colors.grey[400],
                    position: 'right'
                  }}
                />
                
                {/* Stamina Score Line */}
                <Line 
                  type="monotone" 
                  dataKey="staminaScore" 
                  stroke={colors.blueAccent[400]} 
                  strokeWidth={2}
                  name={t('staminaScore', 'Stamina Score')}
                  dot={false}
                  activeDot={{ r: 6 }}
                  opacity={0.5}
                />
                
                {/* Polynomial Trend Line */}
                {trendData && (
                  <Line 
                    type="monotone" 
                    dataKey="trend" 
                    stroke={colors.greenAccent[500]} 
                    strokeWidth={3}
                    name={t('staminaTrend', 'Trend')}
                    dot={false}
                    strokeDasharray="5 5"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default StaminaChart;
